/**
 * usePWPreviousLogic.js
 * React hook for Pregnant Women Previous Vaccination scenario
 *
 * Phase 2: Custom hook that encapsulates PW Previous vaccination logic
 * Returns stable DoseConfig contract for UI components (checkbox + date picker)
 */

import { useMemo, useCallback } from 'react';
import {
  getPWPreviousDoseStatus,
  validateTD2DateAfterTD1Change,
  getDatePickerConstraints
} from './PWPreviousRules';

/**
 * Custom hook for PW Previous vaccination logic
 * Provides stable contract interface for UI components with checkbox and date picker logic
 * @param {Object} vaccine - Vaccine object with id, name, availableDoses
 * @param {Object} profileData - Profile data with lmc_date and vaccination history
 * @param {Function} decryptFunction - Function to decrypt dose numbers
 * @param {Object} formState - Current form state for cross-dose validation
 * @returns {Object} - Hook result with getDoseConfig and validation functions
 */
export const usePWPreviousLogic = (
  vaccine,
  profileData,
  decryptFunction,
  formState = null,
  identityContext = null
) => {
  // Memoize the dose config calculation to prevent unnecessary re-renders
  const getDoseConfig = useMemo(() => {
    return (doseIndex) => {
      try {
        // Get dose status using pure business logic
        const doseStatus = getPWPreviousDoseStatus(
          vaccine,
          doseIndex,
          profileData,
          decryptFunction,
          formState,
          identityContext
        );

        // Return stable DoseConfig contract for Previous vaccination (checkbox + date picker)
        return {
          // Core checkbox properties
          checkboxEnabled: doseStatus.checkboxEnabled || false,
          showCheckbox: doseStatus.showCheckbox !== false, // Default to true

          // Date picker properties
          showDatePicker: doseStatus.showDatePicker || false,
          minDate: doseStatus.minDate || null,
          maxDate: doseStatus.maxDate || null,

          // Status and display properties
          status: doseStatus.status || 'Unknown',
          date: doseStatus.date || null,
          reason: doseStatus.reason || null,
          isCalculated: doseStatus.isCalculated || false,

          // Metadata for debugging/logging
          scenario: 'pw-previous',
          vaccineId: vaccine.id,
          vaccineName: vaccine.name,
          doseIndex
        };
      } catch (error) {
        console.error('Error in usePWPreviousLogic.getDoseConfig:', error);

        // Return safe fallback state
        return {
          checkboxEnabled: false,
          showCheckbox: true,
          showDatePicker: false,
          minDate: null,
          maxDate: null,
          status: 'Error',
          date: null,
          reason: 'Calculation error',
          isCalculated: false,
          scenario: 'pw-previous',
          vaccineId: vaccine.id,
          vaccineName: vaccine.name,
          doseIndex
        };
      }
    };
  }, [vaccine, profileData, decryptFunction, formState, identityContext]);

  // Date picker constraints helper
  const getDateConstraints = useCallback((doseIndex) => {
    const doseConfig = getDoseConfig(doseIndex);
    return getDatePickerConstraints(doseConfig);
  }, [getDoseConfig]);

  // TD cascade validation helper
  const validateTD2Cascade = useCallback((newTD1Date, currentTD2Date) => {
    const doseGap = vaccine?.doseFrequencies?.[1] !== undefined ? vaccine.doseFrequencies[1] : 28;
    return validateTD2DateAfterTD1Change(newTD1Date, currentTD2Date, doseGap);
  }, [vaccine]);

  // Return stable hook interface
  return {
    getDoseConfig,
    getDateConstraints,
    validateTD2Cascade,
    scenario: 'pw-previous',
    isReady: !!(vaccine && profileData && decryptFunction)
  };
};

/**
 * Enhanced hook for PW Previous vaccination with additional features
 * Includes form integration helpers and batch validation
 * @param {Object} vaccine - Vaccine object
 * @param {Object} profileData - Profile data
 * @param {Function} decryptFunction - Decrypt function
 * @param {Object} formState - Form state object
 * @param {Object} options - Additional options for future extension
 * @returns {Object} - Enhanced hook result
 */
export const usePWPreviousLogicEnhanced = (
  vaccine,
  profileData,
  decryptFunction,
  formState = null,
  identityContext = null,
  options = {}
) => {
  const baseLogic = usePWPreviousLogic(
    vaccine,
    profileData,
    decryptFunction,
    formState,
    identityContext
  );

  // Destructure stable refs BEFORE useMemo so the dependency array only holds
  // referentially-stable values, not the whole baseLogic object wrapper which
  // is re-created on every call even when the underlying functions haven't changed.
  const { getDoseConfig, isReady } = baseLogic;

  // Calculate all dose configs at once for performance
  const allDoseConfigs = useMemo(() => {
    if (!isReady || !vaccine.availableDoses) {
      return [];
    }

    return vaccine.availableDoses.map((dose, index) =>
      getDoseConfig(index)
    );
  }, [getDoseConfig, isReady, vaccine.availableDoses]);

  // Form integration helpers
  const formHelpers = useMemo(() => {
    return {
      // Check if any doses are selected
      hasSelectedDoses: allDoseConfigs.some(config =>
        formState?.doses?.[config.doseIndex]?.selected === true
      ),

      // Get all selected doses with dates
      getSelectedDosesWithDates: () => {
        if (!formState?.doses) return [];

        return allDoseConfigs
          .map((config, index) => ({
            ...config,
            selected: formState.doses[index]?.selected || false,
            administeredDate: formState.doses[index]?.administeredDate || null
          }))
          .filter(dose => dose.selected && dose.administeredDate);
      },

      // Validate all selected doses
      validateAllDoses: () => {
        const selectedDoses = formHelpers.getSelectedDosesWithDates();
        const errors = [];

        // Check TD cascade validation for TD vaccines
        if (selectedDoses.length >= 2) {
          const td1Dose = selectedDoses.find(d => d.doseIndex === 0);
          const td2Dose = selectedDoses.find(d => d.doseIndex === 1);

          if (td1Dose && td2Dose) {
            const cascadeResult = baseLogic.validateTD2Cascade(
              td1Dose.administeredDate,
              td2Dose.administeredDate
            );

            if (!cascadeResult.isValid) {
              errors.push({
                doseIndex: 1,
                message: cascadeResult.reason
              });
            }
          }
        }

        return {
          isValid: errors.length === 0,
          errors
        };
      }
    };
  }, [allDoseConfigs, formState, baseLogic]);

  return {
    ...baseLogic,
    allDoseConfigs,
    formHelpers,

    // Convenience methods
    isDoseCheckboxEnabled: (doseIndex) => {
      return allDoseConfigs[doseIndex]?.checkboxEnabled || false;
    },

    shouldShowDatePicker: (doseIndex) => {
      const config = allDoseConfigs[doseIndex];
      const isSelected = formState?.doses?.[doseIndex]?.selected || false;
      return config?.showDatePicker && isSelected;
    },

    getDoseStatus: (doseIndex) => {
      return allDoseConfigs[doseIndex]?.status || 'Unknown';
    },

    // Status summary
    hasAdministeredDoses: allDoseConfigs.some(config => config.status === 'administered'),
    hasEnabledDoses: allDoseConfigs.some(config => config.checkboxEnabled),
    hasDisabledDoses: allDoseConfigs.some(config => !config.checkboxEnabled),

    // Debug info
    debugInfo: options.debug ? {
      vaccine: vaccine.name,
      lmcDate: profileData?.lmc_date,
      vaccinationCount: profileData?.vaccination?.length || 0,
      formState: formState ? {
        selectedCount: Object.values(formState.doses || {}).filter(d => d.selected).length
      } : null
    } : null
  };
};

/**
 * Specialized hook for TD vaccine cascade validation
 * Focuses specifically on TD-1/TD-2 date relationship validation
 * @param {Object} vaccine - Vaccine object
 * @param {Object} profileData - Profile data
 * @param {Function} decryptFunction - Decrypt function
 * @returns {Object} - TD cascade validation utilities
 */
export const useTDCascadeValidation = (vaccine, profileData, decryptFunction) => {
  const baseLogic = usePWPreviousLogic(vaccine, profileData, decryptFunction);

  return {
    // Validate TD-2 date when TD-1 changes
    validateTD2WhenTD1Changes: useCallback((newTD1Date, currentTD2Date) => {
      const doseGap = vaccine?.doseFrequencies?.[1] !== undefined ? vaccine.doseFrequencies[1] : 28;
      const result = validateTD2DateAfterTD1Change(newTD1Date, currentTD2Date, doseGap);
      return {
        ...result,
        shouldClearTD2: !result.isValid,
        warningMessage: !result.isValid ?
          `TD-2 date cleared because it is less than ${doseGap} days from new TD-1 date` : null
      };
    }, [vaccine]),

    // Check if TD-2 should be disabled based on TD-1 date
    shouldDisableTD2: useCallback((td1Date) => {
      if (!td1Date) return true;

      const td2Config = baseLogic.getDoseConfig(1);
      return td2Config.status === 'no_dates_available' || !td2Config.checkboxEnabled;
    }, [baseLogic])
  };
};